import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { accountSsoPublic, adminMe } from '../api/admin';
import type { SsoChoice } from '../api/admin';
import { AuthSplit, WhatsNew, initialsOf } from './AuthLayout';

/**
 * One account's own sign-in page, at /login/:accountSlug.
 *
 * The difference from /login is that this page already knows who you are with,
 * so it asks nothing. That matters in two places the shared page cannot reach:
 * a link an IT department can publish on an intranet or an IdP tile, and a
 * domain two accounts both claim — where discovery has to pick one and this
 * link does not.
 *
 * It is an addition, never a replacement. /login still does domain discovery
 * for everyone who arrives knowing only their email address.
 *
 * R13 (ABC5-05): the sign-in frame with the account's tile and name at the
 * top of the form column.
 */
export default function AccountLoginPage() {
  const { accountSlug = '' } = useParams();
  const [state, setState] = useState<
    { kind: 'loading' }
    | { kind: 'ready'; accountName: string; ssoRequired: boolean; providers: SsoChoice[] }
    | { kind: 'unknown' }
  >({ kind: 'loading' });
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Already signed in? Then this page has nothing to offer.
    adminMe()
      .then((r) => navigate(r.isPlatformAdmin && !r.masquerading ? '/platform' : '/admin', { replace: true }))
      .catch(() => undefined);

    accountSsoPublic(accountSlug)
      .then((r) => setState({
        kind: 'ready', accountName: r.accountName, ssoRequired: r.ssoRequired, providers: r.providers,
      }))
      // A slug that is not an SSO account is not worth distinguishing from one
      // that does not exist — both send you to the page that can work it out.
      .catch(() => setState({ kind: 'unknown' }));

    try {
      const err = new URLSearchParams(window.location.search).get('sso_error');
      if (err) { setError(err); window.history.replaceState({}, '', window.location.pathname); }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountSlug]);

  let body: React.ReactNode;
  if (state.kind === 'loading') {
    body = <p className="auth-lede">Loading…</p>;
  } else if (state.kind === 'unknown') {
    body = (
      <div className="auth-stack">
        <h1 className="auth-title">Check your sign-in link</h1>
        {error && <div className="auth-err" role="alert">{error}</div>}
        <p className="auth-lede">
          No single sign-on is set up at this address. If your company uses SSO, check the link you
          were given — otherwise sign in with your email and password.
        </p>
        <Link className="fs-btn ink lg block" to="/login">Go to sign in</Link>
      </div>
    );
  } else {
    const { accountName, ssoRequired, providers } = state;
    body = (
      <div className="auth-stack">
        {accountName && <span className="auth-initials" aria-hidden="true">{initialsOf(accountName)}</span>}
        <h1 className="auth-title">{accountName}</h1>
        <p className="auth-lede">Sign in to Form-Magic with your {accountName} account.</p>

        {error && <div className="auth-err" role="alert">{error}</div>}

        <div className="auth-sso">
          {(providers || []).map((p) => (
            <a key={p.slug} className="fs-btn primary block" href={p.loginUrl}>Continue with {p.name}</a>
          ))}
        </div>

        {/* Where SSO is optional a password still works, and this page must not
            pretend otherwise — it would strand the very people it is for. Where
            SSO is required only a break-glass user has one, so the wording
            points at the exception rather than offering it as a normal way in. */}
        <div className="auth-links">
          <Link to="/login">{ssoRequired ? 'Break-glass sign-in' : 'Sign in with a password instead'}</Link>
        </div>
      </div>
    );
  }

  return <AuthSplit aside={<WhatsNew />}>{body}</AuthSplit>;
}
